import {
	Component,
	AfterViewInit,
	OnInit,
	EventEmitter,
	Output,
	NgZone,
	Input,
	ChangeDetectorRef,
} from "@angular/core";
import {
	Map,
	LatLng,
	LatLngBounds,
	Marker,
	Icon,
	latLng,
	icon as leafletIcon,
	marker as leafletMarker,
	tileLayer,
} from "leaflet";
import { ActivatedRoute, Router } from "@angular/router";
import { Policies } from "src/app/_core/models/main/policies.enum";

//services
import { TranslateService } from "@ngx-translate/core";
import { LoaderService } from "src/app/_core/providers/loader.service";
import { GlobalService } from "src/app/_core/providers/global.service";

//models
import { SiteSummaryViewModel } from "src/app/_core/models/master/site-summary.model";
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { SiteViewModel } from "src/app/_core/models/master/site.model";
import { Title } from "@angular/platform-browser";
import { SiteService } from "src/app/_core/providers/site.service";
import { CentrosProcedureViewModel } from "src/app/_core/models/master/centro.model";
import { CentrosService } from "src/app/_core/providers/centros.service";
import { Subject, takeUntil } from "rxjs";
import { CebeViewModel } from "src/app/_core/models/centros/cebe.model";
import {
	MAP_PIN_ICONS,
	getIconByInstallationType,
	DEFAULT_VIEW_OPTIONS,
} from "src/app/_core/constants/leaflet-map.constants";

@Component({
	selector: "app-mini-map",
	templateUrl: "./mini-map.component.html",
	styleUrls: ["./mini-map.component.scss"],
})
export class MiniMapComponent implements OnInit, AfterViewInit {
	public contextLocation: ContextLocationViewModel;

	@Input() cebeId: any;
	@Input() cebe: CebeViewModel;
	@Input() loadByCebe = false;
	private readonly destroy$ = new Subject<void>();

	// Maps
	public options: any;
	public map: Map;
	public layers: any = [];
	public center: LatLng;
	public sites: Array<CentrosProcedureViewModel> = [];
	public allSites: Array<CentrosProcedureViewModel>;
	public fitBounds: LatLngBounds;
	public markerClusterGroup: any; // MarkerClusterGroup comes from plugin
	public markerClusterData: any[] = [];
	public markerClusterOptions: any; // MarkerClusterGroupOptions comes from plugin

	// Filter
	public filterOptions: string[] = [];
	public filterSelection: string[] = [];

	// Selection
	public displayDialog: boolean = false;
	public selectedItem: CentrosProcedureViewModel;
	private readonly currentMarker: L.Marker;

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _zone: NgZone,
		private readonly _router: Router,
		private readonly _global: GlobalService,
		private readonly _loaderService: LoaderService,
		private readonly _centrosService: CentrosService,
		private readonly _siteService: SiteService,
		private readonly _cd: ChangeDetectorRef,
	) {}

	// Events
	ngOnInit(): void {
		console.log("MiniMap - cebeId:", this.cebeId);
		console.log("MiniMap - cebe:", this.cebe);

		this.markerClusterOptions = { maxClusterRadius: 50 };
		this.contextLocation = {
			country: "",
			countryName: "",
			service: "",
			serviceName: "",
			site: "",
			siteName: "",
		};

		// Setup map options first (needed for map creation)
		this._setupMapSettings();

		if (this.cebeId) {
			this._loadDataAndMap();
		}

		setInterval(() => {
			if (this.map) {
				this.map.invalidateSize();
			}
		}, 500);
	}

	ngAfterViewInit(): void {}

	// Public methods
	onMapReady(map: L.Map) {
		console.log("Map ready!");
		this.map = map;
		window.onload = window.onresize = function () {
			map.invalidateSize();
		};

		// Intentar dibujar el mapa si los sites ya están cargados
		this._tryDrawMap();
	}

	public ajusteDeZoom() {
		this.map.invalidateSize();
	}

	openDialog(site: CentrosProcedureViewModel) {
		if (site && site.codigo) {
			this.displayDialog = true;
			this.selectedItem = site;
			this._cd.detectChanges();

			// Ajustar el mapa después de que el diálogo se haya renderizado
			setTimeout(() => {
				this.ajusteDeZoom();
			}, 100);
		}
	}

	goToSite(site: SiteViewModel) {
		if (site.hasAccess) {
			this._router.navigate([
				`/service/${site.serviceCode}/site/${site.code}`,
			]);
		}
	}

	closeDialog() {
		this.displayDialog = false;
		this.selectedItem = null;
		this.layers.forEach((layer: L.Marker) => {
			layer.closePopup();
		});

		// Ajustar el mapa después de cerrar el diálogo
		setTimeout(() => {
			this.ajusteDeZoom();
		}, 100);
	}

	filterAllTypes() {
		if (this.filterSelection.length > 0) {
			this.filterSelection = [];
			this._filterSites();
		}
	}

	filterType(option: string) {
		const index = this.filterSelection.indexOf(option);
		if (index == -1) {
			this.filterSelection.push(option);
		} else {
			this.filterSelection.splice(index, 1);
		}
		this._filterSites();
	}

	// Private methods
	private _loadDataAndMap(): void {
		this._loadSites();
	}

	private _setupMapSettings() {
		this.options = {
			layers: [
				tileLayer("http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
					maxZoom: 18,
					minZoom: 3,
					attribution: "",
				}),
			],
			zoomControl: false,
			attributionControl: false,
		};
	}

	private _initMap(): void {
		if (!this.map) {
			console.log("Map not ready yet, skipping _initMap");
			return;
		}

		console.log("Initializing map with sites:", this.sites);
		const newLayers = []; // Crear array temporal
		const arrayOfLatLngs = [];

		// Default to Spain's coordinates if cebe doesn't have location data
		const defaultLat = this.cebe?.latitudPais || "40.4168";
		const defaultLng = this.cebe?.longitudPais || "-3.7038";

		const defaultView = {
			center: latLng(+defaultLat, +defaultLng),
			zoom: DEFAULT_VIEW_OPTIONS.zoom,
		};

		const {
			pin01: pinIcon01,
			pin02: pinIcon02,
			pin03: pinIcon03,
			pin04: pinIcon04,
			pin05: pinIcon05,
			pin06: pinIcon06,
			pin07: pinIcon07,
		} = MAP_PIN_ICONS;

		for (const site of this.sites) {
			if (!site.latitud || !site.longitud) {
				continue;
			}
			const icon = getIconByInstallationType(site.tipo_Instalacion);

			arrayOfLatLngs.push([
				parseFloat(site.latitud),
				parseFloat(site.longitud),
			]);

			var component = this;
			newLayers.push(
				leafletMarker(
					[parseFloat(site.latitud), parseFloat(site.longitud)],
					{
						icon,
					},
				)
					.bindPopup(site.descripcion)
					.openPopup()
					.on("click", () => {
						component._zone.run(() => {
							component.openDialog(site);
						});
					}),
			);
		}

		// Asignar el nuevo array para cambiar la referencia y activar change detection
		this.layers = newLayers;
		console.log("Layers updated, forcing change detection");
		this._cd.detectChanges();

		// If there are no sites, set the default view to Spain
		if (arrayOfLatLngs.length === 0) {
			console.log(
				"No sites with valid coordinates, setting default view",
			);
			this.map.setView(defaultView.center, defaultView.zoom);
		} else {
			console.log(
				`Setting bounds for ${arrayOfLatLngs.length} sites, layers count: ${this.layers.length}`,
			);
			this.fitBounds = new LatLngBounds(arrayOfLatLngs);
			this.map.fitBounds(this.fitBounds);
		}
	}

	private _loadSites(): void {
		console.log(
			"Loading sites for",
			this.loadByCebe ? "cebeId" : "contractId",
			":",
			this.cebeId,
		);
		const sites$ = this.loadByCebe
			? this._centrosService.getCentroByCebeId(this.cebeId)
			: this._centrosService.getCentroByContratosId(this.cebeId);
		sites$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (sites: Array<CentrosProcedureViewModel>) => {
				console.log("Sites loaded:", sites);
				sites.forEach((s) => {
					const partes = s.codigoTipoInstalacion
						? s.codigoTipoInstalacion.split("_")
						: null;
					if (partes) {
						s.tipo_Instalacion =
							partes.length > 1 ? partes[1] : partes[0];
					}
				});
				this.allSites = sites.filter(
					(x: CentrosProcedureViewModel) =>
						x.latitud &&
						x.longitud &&
						parseFloat(x.latitud) >= -90 &&
						parseFloat(x.latitud) <= 90 &&
						parseFloat(x.longitud) >= -180 &&
						parseFloat(x.longitud) <= 180,
				);
				console.log(
					"Filtered sites with valid coordinates:",
					this.allSites,
				);
				this.filterOptions = [
					...new Set(
						this.allSites
							.filter(
								(p) =>
									p.tipo_Instalacion &&
									p.tipo_Instalacion.trim() !== "",
							)
							.map((p) => p.tipo_Instalacion),
					),
				].sort((a, b) => (a > b ? 1 : -1));
				/* this.filterOptions = [
					...new Map(
						this.allSites
							.filter(
								(p) =>
									p.codigoTipoInstalacion &&
									p.codigoTipoInstalacion.trim() !== ""
							)
							.map((p) => {
								const partes =
									p.codigoTipoInstalacion.split("_");
								return [
									partes.length > 1 ? partes[1] : partes[0],
									p.codigoTipoInstalacion,
								];
							})
					).values(),
				].sort((a, b) => (a > b ? 1 : -1)); */

				// Intentar dibujar el mapa si ya está listo
				this._tryDrawMap();
				this._loaderService.end();
			},
			error: (error) => {
				console.error("Error loading sites:", error);
				this._loaderService.end();
			},
		});
	}

	private _filterSites() {
		console.log(
			"_filterSites called, filterSelection:",
			this.filterSelection,
		);
		console.log("_filterSites - allSites:", this.allSites);

		if (this.filterSelection.length == 0) {
			this.sites = this.allSites;
		} else {
			this.sites = this.allSites.filter(
				(p) => this.filterSelection.indexOf(p.tipo_Instalacion) > -1,
			);
		}
		console.log("Sites after filter:", this.sites);
		console.log("Map exists?", !!this.map);
		this._initMap();
	}

	/**
	 * Intenta dibujar el mapa solo si tanto el mapa como los sites están listos
	 */
	private _tryDrawMap() {
		console.log(
			"_tryDrawMap - Map ready?",
			!!this.map,
			"Sites ready?",
			!!this.allSites && this.allSites.length > 0,
		);

		if (this.map && this.allSites && this.allSites.length > 0) {
			console.log(
				"Both map and sites are ready, filtering and drawing...",
			);
			this._filterSites();
		}
	}

	isFilterSelected(filter: string) {
		return this.filterSelection.indexOf(filter) > 0;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
